import { useNavigate } from 'react-router-dom';
import type { UnitCategory } from '../../types/admin';

interface UnitCategoryTableProps {
  categories: UnitCategory[];
  selectedIds: number[];
  onSelectionChange: (ids: number[]) => void;
}

export default function UnitCategoryTable({ categories, selectedIds, onSelectionChange }: UnitCategoryTableProps) {
  const navigate = useNavigate();

  // Debug logging
  console.log('UnitCategoryTable - categories:', categories);
  if (categories && categories.length > 0) {
    console.log('First category in table:', categories[0]);
    console.log('Has ucatId?', 'ucatId' in categories[0], categories[0].ucatId);
  }

  // Early return if categories is undefined or null
  if (!categories) {
    return (
      <div className="text-center py-8 text-gray-500">
        Loading unit categories...
      </div>
    );
  }

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      onSelectionChange(categories.map(c => c.ucatId));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectOne = (ucatId: number, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedIds, ucatId]);
    } else {
      onSelectionChange(selectedIds.filter(id => id !== ucatId));
    }
  };

  const handleRowClick = (ucatId: number) => {
    navigate(`/admin/units/${ucatId}/edit`);
  };

  const allSelected = categories.length > 0 && selectedIds.length === categories.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < categories.length;

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left">
              <input
                type="checkbox"
                checked={allSelected}
                ref={input => {
                  if (input) input.indeterminate = someSelected;
                }}
                onChange={handleSelectAll}
                className="rounded border-gray-300"
              />
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">ID</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Name Long</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Name Short</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Base Unit</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">User Unit</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Precision</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Formulas</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {categories.map((category) => (
            <tr
              key={category.ucatId}
              onClick={() => handleRowClick(category.ucatId)}
              className="hover:bg-blue-50 cursor-pointer transition-colors duration-150"
            >
              <td
                className="px-4 py-2"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.includes(category.ucatId)}
                  onChange={(e) => handleSelectOne(category.ucatId, e.target.checked)}
                  className="rounded"
                />
              </td>
              <td className="px-4 py-2 whitespace-nowrap">
                <span className="text-blue-600 font-medium">{category.ucatId}</span>
              </td>
              <td className="px-4 py-2">{category.nameLong}</td>
              <td className="px-4 py-2 whitespace-nowrap">{category.nameShort}</td>
              <td className="px-4 py-2 whitespace-nowrap">{category.baseUnitName}</td>
              <td className="px-4 py-2 whitespace-nowrap">{category.userUnitName}</td>
              <td className="px-4 py-2 whitespace-nowrap">{category.userUnitPrecision}</td>
              <td className="px-4 py-2 whitespace-nowrap">{category.formulaCount}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
